import ErrorAlert from "@/components/ErrorAlert";
import { useTranslation } from "@/i18n";
import { PrecheckResponse } from "@/lib/actions";
import { FileDown, Info, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { ProviderSelector } from "./ProviderSelector";

/**
 * Props for the DocumentSelection component.
 */
interface DocumentSelectionProps {
  /** Currently selected file, or null if none */
  file: File | null;
  /** Whether the component is in a busy/loading state */
  busy: boolean;
  /** Error message to display, if any */
  error: string | null;
  /** Callback when file selection changes */
  onFileChange: (file: File | null) => void;
  /** Callback when form is submitted */
  onSubmit: (e: React.FormEvent) => void;
  /** Result of precheck validation, null if not yet completed */
  precheckResult: PrecheckResponse | null;
  /** Whether precheck is currently loading */
  precheckLoading: boolean;
}

/**
 * Document selection component for the first step of the upload workflow.
 * Allows users to select a PDF or DOCX file via drag-and-drop or file browser,
 * and choose between AI providers (Anthropic or Groq).
 *
 * The selected AI provider is persisted to localStorage for future sessions.
 *
 * @param props - Component props
 * @returns Document selection form UI
 */
export default function DocumentSelection({
  file,
  busy,
  error,
  onFileChange,
  onSubmit,
  precheckResult,
  precheckLoading,
}: DocumentSelectionProps) {
  const { t } = useTranslation();
  const [aiProvider, setAiProvider] = useState<string>("anthropic");

  // Determine if the button should be disabled
  const isButtonDisabled =
    busy || !file || precheckLoading || !precheckResult?.valid;

  /**
   * Translates precheck check keys to human-readable German messages.
   */
  const getHumanReadableMessage = (): string => {
    if (!precheckResult) {
      return t("documentUpload.selection.errorInsufficientPermissions");
    }

    // Check which specific checks failed and provide appropriate messages
    const { checks } = precheckResult;
    const failedChecks: string[] = [];

    if (checks.token_valid === false) {
      failedChecks.push(t("documentUpload.selection.errorInvalidSession"));
    }
    if (checks.tenant_exists === false) {
      failedChecks.push(t("documentUpload.selection.errorTenantNotFound"));
    }
    if (checks.user_is_admin_or_manager === false) {
      failedChecks.push(
        t("documentUpload.selection.errorInsufficientPermissions")
      );
    }
    if (checks.forge_permission_enabled === false) {
      failedChecks.push(t("documentUpload.selection.errorForgeNotEnabled"));
    }
    if (checks.license_active === false) {
      failedChecks.push(t("documentUpload.selection.errorLicenseInactive"));
    }
    if (checks.license_not_expired === false) {
      failedChecks.push(t("documentUpload.selection.errorLicenseExpired"));
    }
    if (checks.license_not_terminated === false) {
      failedChecks.push(t("documentUpload.selection.errorLicenseCancelled"));
    }

    if (failedChecks.length > 0) {
      return failedChecks.join(" ");
    }

    // Fallback to missing_requirements or message
    if (
      precheckResult.missing_requirements &&
      precheckResult.missing_requirements.length > 0
    ) {
      return precheckResult.missing_requirements.join(" ");
    }

    return (
      precheckResult.message ||
      t("documentUpload.selection.errorInsufficientPermissions")
    );
  };

  // Load AI provider preference from localStorage on component mount
  useEffect(() => {
    const savedProvider = localStorage.getItem("aiio_ai_provider");
    if (savedProvider) {
      setAiProvider(savedProvider);
    }
  }, []);

  /**
   * Updates AI provider selection and persists to localStorage.
   *
   * @param provider - The selected AI provider
   */
  const handleProviderChange = (provider: string) => {
    setAiProvider(provider);
    localStorage.setItem("aiio_ai_provider", provider);
  };

  /**
   * Handles file drop event for drag-and-drop upload.
   * Validates file type (PDF, DOC, DOCX) before accepting.
   *
   * @param e - Drag event
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (
        file.type === "application/pdf" ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".doc") ||
        file.name.endsWith(".docx")
      ) {
        onFileChange(file);
      }
    }
  };

  /**
   * Prevents default drag-over behavior to enable drop.
   *
   * @param e - Drag event
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  /**
   * Checks if the selected file is a DOCX file.
   */
  const isDocxFile =
    file && (file.name.endsWith(".docx") || file.name.endsWith(".doc"));

  return (
    <div className="w-full max-w-[594px] bg-transparent rounded-lg space-y-5 mx-auto">
      {/* Title */}
      <div className="text-[#18181b] text-base font-normal text-left space-y-2">
        <p className="font-semibold">
          {t("documentUpload.selection.title")}
        </p>
        <p className="text-sm">
          {t("documentUpload.selection.instructionsText")}{" "}
          {t("documentUpload.selection.instructionsPdf")}{" "}
          {t("documentUpload.selection.instructionsDocx")}
        </p>
      </div>

      {/* File Selection Section */}
      <div className="space-y-2">
        <label className="text-[#18181b] text-sm block">
          {t("common.buttons.selectFile")}
        </label>

        {/* Drag and Drop Area */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border border-dashed border-[#e4e4e7] rounded-lg p-5 flex flex-col items-center justify-center bg-[#f4f4f5] min-h-[80px]"
        >
          <div className="flex flex-col items-center gap-1">
            <FileDown size={20} className="text-[#3f3f46]" />
            <span className="text-[#3f3f46] text-xs text-center">
              {t("documentUpload.selection.dragDropPrefix")}{" "}
              <label className="underline cursor-pointer hover:text-[#18181b]">
                {t("documentUpload.selection.browseFiles")}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
            </span>
            <span className="text-[#71717a] text-[10px] mt-1">Max 2 MB</span>
          </div>
        </div>

        {file && (
          <div className="text-[#18181b] text-sm mt-2">
            {t("documentUpload.selection.selectedFile", { filename: file.name })}
          </div>
        )}

        {error && <ErrorAlert error={error} className="mt-3" />}
      </div>

      {/* AI Provider Selection */}
      <div className="space-y-3">
        <label className="text-[#18181b] text-sm block">
          {t("documentUpload.selection.analysisSettings")}
        </label>
        <ProviderSelector
          value={aiProvider}
          onChange={handleProviderChange}
        />
        {/* DOCX Image Guidance */}
        {isDocxFile && (
          <div className="flex items-start gap-2 p-3 bg-[#fef3c7] border border-[#fcd34d] rounded-lg">
            <Info className="w-4 h-4 text-[#d97706] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#92400e] font-medium">
                {t("documentUpload.selection.docxImageRequirements")}
              </p>
              <p className="text-xs text-[#78350f] mt-1">
                {t("documentUpload.selection.docxImageWarning")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Precheck Status */}
      {precheckLoading && (
        <div className="flex items-center gap-2 text-[#71717a] text-sm">
          <Loader2 size={16} className="animate-spin" />
          <span>{t("documentUpload.selection.checkingPermissions")}</span>
        </div>
      )}

      {/* Precheck Info Box */}
      {!precheckLoading && precheckResult && !precheckResult.valid && (
        <div className="flex items-start gap-3 p-4 bg-[#f0f9ff] border border-[#bae6fd] rounded-lg">
          <Info size={20} className="text-[#0284c7] flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-[#0c4a6e]">
              {t("documentUpload.selection.notice")}
            </span>
            <span className="text-sm text-[#0369a1]">
              {getHumanReadableMessage()}
            </span>
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div className="flex justify-end">
        <button
          onClick={(e) => {
            // Ensure provider is saved to localStorage before submitting
            localStorage.setItem("aiio_ai_provider", aiProvider);
            onSubmit(e);
          }}
          disabled={isButtonDisabled}
          className={`px-3 py-[6px] rounded-lg text-sm font-semibold border-none transition-colors ${
            !isButtonDisabled
              ? "bg-[#8e2bf0] text-white hover:bg-[#8e2bf0] cursor-pointer"
              : "bg-[#e4e4e7] text-[#a1a1aa] cursor-not-allowed"
          }`}
        >
          {precheckLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {t("common.checking")}
            </span>
          ) : (
            t("common.buttons.continue")
          )}
        </button>
      </div>
    </div>
  );
}
